import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, type FormEvent } from 'react'
import { api, ApiError, LABEL_PALETTE, type LabelView } from '../lib/api'
import { Banner, Button, Card, TextField } from '../ui'
import { ReadOnlyNotice } from './ReadOnlyNotice'

/**
 * Where a workspace names its own tags. Before this screen existed a label could only be
 * created by seeding it at provisioning time — an admin who wanted a new one had to ask for a
 * manual database edit. This is the first UI that lets them add one themselves.
 */
export function LabelSettings() {
  const queryClient = useQueryClient()
  const [creating, setCreating] = useState(false)
  const [name, setName] = useState('')
  const [color, setColor] = useState(LABEL_PALETTE[0])
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editName, setEditName] = useState('')
  const [editColor, setEditColor] = useState('')

  const team = useQuery({ queryKey: ['team'], queryFn: api.team, retry: false })
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['team'] })

  const create = useMutation({
    mutationFn: () => api.createLabel({ name: name.trim(), color }),
    onSuccess: async () => {
      setName('')
      setColor(LABEL_PALETTE[0])
      setCreating(false)
      await refresh()
    },
  })

  const update = useMutation({
    mutationFn: (input: { id: string; name: string; color: string }) =>
      api.updateLabel(input.id, { name: input.name, color: input.color }),
    onSuccess: async () => {
      setEditingId(null)
      await refresh()
    },
  })

  const remove = useMutation({ mutationFn: api.deleteLabel, onSuccess: refresh })

  if (team.isPending) return <div className="cad-card" style={{ height: '200px' }} />
  if (team.isError) {
    const message = team.error instanceof ApiError ? team.error.message : 'Labels unavailable.'
    return (
      <div className="cad-card">
        <p className="cad-muted">{message}</p>
      </div>
    )
  }

  const labels: LabelView[] = team.data!.labels
  const canManage = team.data!.canManage
  const createError = create.error instanceof ApiError ? create.error.message : undefined

  return (
    <>
      {!canManage && <ReadOnlyNotice what="labels" />}
      {/* The active tab above already says "Labels" — no need to repeat it as a title. */}
      <p className="cad-muted" style={{ maxWidth: '62ch' }}>
        Tags your team puts on a conversation — used for filtering the inbox, scoping who can
        see a chat, and grouping metrics. Every label here is available everywhere labels show
        up in the app.
      </p>

      {canManage && (
        <div className="row">
          {!creating ? (
            <Button variant="tonal" onClick={() => setCreating(true)}>
              Create label
            </Button>
          ) : (
            <Card variant="outlined">
              <form
                className="stack gap-3"
                onSubmit={(event: FormEvent) => {
                  event.preventDefault()
                  if (name.trim()) create.mutate()
                }}
              >
                <TextField label="Name" value={name} onChange={(e) => setName(e.target.value)} placeholder="VIP" />
                <div className="stack gap-1">
                  <span className="cad-muted" style={{ fontSize: 'var(--cad-text-base)' }}>Color</span>
                  <div className="row gap-2 wrap">
                    {LABEL_PALETTE.map((swatch) => (
                      <button
                        key={swatch}
                        type="button"
                        onClick={() => setColor(swatch)}
                        aria-label={`Choose ${swatch}`}
                        aria-pressed={color === swatch}
                        className="cad-swatch"
                        data-selected={color === swatch}
                        style={{ background: swatch }}
                      />
                    ))}
                  </div>
                </div>
                {createError && <Banner tone="error">{createError}</Banner>}
                <div className="row gap-2">
                  <Button type="submit" disabled={!name.trim() || create.isPending}>
                    {create.isPending ? 'Creating…' : 'Create'}
                  </Button>
                  <Button
                    variant="text"
                    onClick={() => {
                      setCreating(false)
                      setName('')
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </Card>
          )}
        </div>
      )}

      <section className="cad-card">
        {(update.error || remove.error) && (
          <Banner tone="error">
            {(() => {
              const err = update.error ?? remove.error
              return err instanceof ApiError || err instanceof Error ? err.message : 'That did not go through.'
            })()}
          </Banner>
        )}
        {labels.length === 0 ? (
          <p className="cad-muted">No labels yet. Create the first one above.</p>
        ) : (
          <div className="cad-grid-frame">
            <table className="cad-table">
              <thead>
                <tr>
                  <th>Label</th>
                  <th>Color</th>
                  {canManage && <th />}
                </tr>
              </thead>
              <tbody>
                {labels.map((label) => (
                  <tr key={label.id}>
                    <td>
                      {editingId === label.id ? (
                        <input
                          className="cad-input"
                          value={editName}
                          onChange={(e) => setEditName(e.target.value)}
                          style={{ maxWidth: '220px' }}
                        />
                      ) : (
                        <span className="cad-pill" style={{ background: `${label.color}22`, color: label.color }}>
                          {label.name}
                        </span>
                      )}
                    </td>
                    <td>
                      {editingId === label.id ? (
                        <div className="row gap-1 wrap">
                          {LABEL_PALETTE.map((swatch) => (
                            <button
                              key={swatch}
                              type="button"
                              onClick={() => setEditColor(swatch)}
                              aria-label={`Choose ${swatch}`}
                              aria-pressed={editColor === swatch}
                              className="cad-swatch"
                              data-selected={editColor === swatch}
                              style={{ background: swatch }}
                            />
                          ))}
                        </div>
                      ) : (
                        <span className="cad-swatch" style={{ background: label.color }} aria-hidden="true" />
                      )}
                    </td>
                    {canManage && (
                      <td>
                        {editingId === label.id ? (
                          <div className="row gap-2">
                            <button
                              type="button"
                              className="cad-btn"
                              data-variant="text"
                              disabled={!editName.trim() || update.isPending}
                              onClick={() => update.mutate({ id: label.id, name: editName.trim(), color: editColor })}
                            >
                              Save
                            </button>
                            <button type="button" className="cad-btn" data-variant="text" onClick={() => setEditingId(null)}>
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <div className="row gap-2">
                            <button
                              type="button"
                              className="cad-btn"
                              data-variant="text"
                              onClick={() => {
                                setEditingId(label.id)
                                setEditName(label.name)
                                setEditColor(label.color)
                              }}
                            >
                              Edit
                            </button>
                            <button
                              type="button"
                              className="cad-btn"
                              data-variant="text"
                              disabled={remove.isPending}
                              onClick={() => {
                                if (confirm(`Delete "${label.name}"? It will disappear from every chat that carries it.`)) {
                                  remove.mutate(label.id)
                                }
                              }}
                            >
                              Delete
                            </button>
                          </div>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </>
  )
}
